import React, { useState, useEffect } from 'react';
import { resolveUserDisplayNames } from '../../../lib/api/audit';
import { logger } from '../../../lib/logger';

interface AuditWatermarkProps {
  createdBy?: string | null;
  createdAt?: string | null;
  completedBy?: string | null;
  completedAt?: string | null;
  confirmedBy?: string | null;
  realizedBy?: string | null;
  entityType: 'propuesta' | 'gestion' | 'reunion';
}

/**
 * Format an ISO timestamp to a short Spanish date string.
 * Handles both ISO date-only (YYYY-MM-DD) and full ISO timestamps.
 * Examples:
 *   "2026-01-15T18:30:00.000Z" -> "15 ene 2026"
 *   "2026-01-15" -> "15 ene 2026"
 */
function formatTimestamp(isoString: string): string {
  const monthNames = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

  try {
    // Handle both "YYYY-MM-DD" and full ISO strings
    if (isoString.length === 10 && isoString.includes('-')) {
      // Date-only format: parse directly to avoid timezone issues
      const [year, month, day] = isoString.split('-').map(Number);
      return `${day} ${monthNames[month - 1]} ${year}`;
    }

    // Full ISO timestamp: use Date object
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return isoString;

    const day = date.getDate();
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  } catch {
    return isoString;
  }
}

export const AuditWatermark: React.FC<AuditWatermarkProps> = ({
  createdBy,
  createdAt,
  completedBy,
  completedAt,
  confirmedBy,
  realizedBy,
  entityType,
}) => {
  const [displayNames, setDisplayNames] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(false);

  // Collect all non-null UUIDs that need resolution
  const userIds = [createdBy, completedBy, confirmedBy, realizedBy].filter(
    (id): id is string => !!id
  );

  // If no audit data at all, render nothing
  const hasAnyData = userIds.length > 0;

  useEffect(() => {
    if (!hasAnyData) return;

    let cancelled = false;
    setLoading(true);

    resolveUserDisplayNames(userIds)
      .then((names) => {
        if (!cancelled) {
          setDisplayNames(names);
          setLoading(false);
        }
      })
      .catch((err) => {
        logger.error('[AuditWatermark] Error resolving names:', err);
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdBy, completedBy, confirmedBy, realizedBy]);

  if (!hasAnyData) return null;

  const getName = (uuid: string | null | undefined): string => {
    if (!uuid) return '';
    if (loading) return '...';
    return displayNames.get(uuid) || '...';
  };

  // Build the lines to display
  const lines: { label: string; name: string; date: string }[] = [];

  if (createdBy) {
    lines.push({
      label: 'Creado por',
      name: getName(createdBy),
      date: createdAt ? formatTimestamp(createdAt) : '',
    });
  }

  if (entityType === 'propuesta' || entityType === 'gestion') {
    if (completedBy) {
      lines.push({
        label: 'Completado por',
        name: getName(completedBy),
        date: completedAt ? formatTimestamp(completedAt) : '',
      });
    }
  }

  if (entityType === 'reunion') {
    if (confirmedBy) {
      lines.push({
        label: 'Confirmado por',
        name: getName(confirmedBy),
        date: '', // No timestamp column for confirmation
      });
    }
    if (realizedBy) {
      lines.push({
        label: 'Realizado por',
        name: getName(realizedBy),
        date: '', // No timestamp column for realization
      });
    }
  }

  if (lines.length === 0) return null;

  return (
    <div className="col-span-2 mt-4 pt-4 border-t border-[#E5E7EB]">
      <div className="space-y-1.5">
        {lines.map((line, index) => (
          <div key={index} className="flex items-center text-xs text-[#9CA3AF]">
            <span>
              {line.label}: <span className="font-medium text-[#6B7280]">{line.name}</span>
              {line.date && (
                <span className="ml-1">-- {line.date}</span>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
